/** Fur & Far personal and per-pet wishlists backed by the signed App Proxy. */
(() => {
  if (window.FurFarWishlist) return;

  const DECIMAL_ID = /^[1-9][0-9]{0,18}$/;

  class WishlistStateError extends Error {
    constructor(code) {
      super(code);
      this.name = 'WishlistStateError';
      this.code = code;
    }
  }

  const copy = (value) => JSON.parse(JSON.stringify(value));

  const normalizeProduct = (product) => {
    const productId = String(product && (product.shopify_product_id || product.id) || '');
    const variantValue = product && (product.shopify_variant_id || product.variantId);
    const variantId = variantValue == null || variantValue === '' ? null : String(variantValue);
    const handle = String(product && (product.product_handle || product.handle) || '').trim();
    const title = String(product && (product.product_title || product.title) || '').trim();
    const productUrl = String(product && (product.product_url || product.url) || '').trim();
    let imageUrl = String(product && (product.image_url || product.image) || '').trim();
    if (imageUrl.startsWith('//')) imageUrl = `https:${imageUrl}`;
    const displayPrice = String(product && (product.display_price || product.price) || '').trim();
    if (
      !DECIMAL_ID.test(productId)
      || (variantId !== null && !DECIMAL_ID.test(variantId))
      || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)
      || !title || title.length > 255
      || !productUrl.startsWith('/products/') || productUrl.length > 2048
      || (imageUrl && (!imageUrl.startsWith('https://') || imageUrl.length > 2048))
      || displayPrice.length > 40
    ) {
      throw new WishlistStateError('invalid_product');
    }
    return {
      shopify_product_id: productId,
      shopify_variant_id: variantId,
      product_handle: handle,
      product_title: title,
      product_url: productUrl,
      image_url: imageUrl || null,
      display_price: displayPrice || null,
    };
  };

  const createStore = ({ api, isSignedIn }) => {
    let status = 'idle';
    let pets = [];
    let wishlists = [];

    const setState = (data) => {
      if (!data || !Array.isArray(data.pets) || !Array.isArray(data.wishlists)) {
        throw new WishlistStateError('invalid_server_state');
      }
      pets = data.pets.slice(0, 10);
      const petIds = new Set(pets.map((pet) => String(pet.id)));
      wishlists = data.wishlists.filter((wishlist) => (
        wishlist && wishlist.kind === 'personal'
        || wishlist && wishlist.kind === 'pet' && petIds.has(String(wishlist.pet_id))
      )).map((wishlist) => ({
        ...wishlist,
        items: Array.isArray(wishlist.items) ? wishlist.items : [],
      }));
      status = 'ready';
      return snapshot();
    };

    const snapshot = () => ({ status, pets: pets.slice(), wishlists: wishlists.slice() });

    const defaultListId = (current) => {
      if (current && pets.some((pet) => String(pet.id) === String(current))) return String(current);
      return pets[0] ? String(pets[0].id) : 'personal';
    };

    const listFor = (listId) => listId === 'personal'
      ? wishlists.find((wishlist) => wishlist.kind === 'personal')
      : wishlists.find((wishlist) => wishlist.kind === 'pet' && String(wishlist.pet_id) === String(listId));

    const itemsFor = (listId) => {
      const wishlist = listFor(listId);
      return wishlist ? wishlist.items : [];
    };

    const productMatches = (item, product) => (
      String(item.shopify_product_id) === String(product.shopify_product_id)
      && String(item.shopify_variant_id || '') === String(product.shopify_variant_id || '')
    );

    const toggle = async (requestedListId, product) => {
      if (!isSignedIn) return { ok: false, needsAuth: true };
      const listId = pets.length ? defaultListId(requestedListId) : 'personal';
      const normalized = normalizeProduct(product);
      const previous = copy({ pets, wishlists });
      let wishlist = listFor(listId);
      if (!wishlist) {
        wishlist = {
          id: `optimistic-${listId}`,
          kind: listId === 'personal' ? 'personal' : 'pet',
          pet_id: listId === 'personal' ? null : listId,
          items: [],
        };
        wishlists = [...wishlists, wishlist];
      }
      const existingIndex = wishlist.items.findIndex((item) => productMatches(item, normalized));
      const added = existingIndex === -1;
      const nextItems = wishlist.items.slice();
      if (added) nextItems.unshift({ id: `optimistic-${normalized.shopify_product_id}`, ...normalized });
      else nextItems.splice(existingIndex, 1);
      wishlists = wishlists.map((candidate) => candidate === wishlist
        ? { ...candidate, items: nextItems }
        : candidate);
      status = 'saving';

      try {
        const result = await api.toggleWishlist({
          ...normalized,
          pet_id: listId === 'personal' ? null : listId,
          saved: added,
        });
        if (!result || result.ok !== true) throw new WishlistStateError('invalid_server_state');
        setState(result.data);
        return { ok: true, added, listId };
      } catch (error) {
        pets = previous.pets;
        wishlists = previous.wishlists;
        status = 'error';
        throw error;
      }
    };

    const counts = () => {
      const personal = itemsFor('personal').length;
      const petCounts = pets.map((pet) => ({
        id: pet.id,
        name: pet.pet_name || 'Pet',
        count: itemsFor(String(pet.id)).length,
      }));
      return {
        personal,
        pets: petCounts,
        total: personal + petCounts.reduce((sum, row) => sum + row.count, 0),
      };
    };

    return { counts, defaultListId, itemsFor, normalizeProduct, setState, snapshot, toggle };
  };

  const isSignedIn = document.body.dataset.customerSignedIn === 'true';
  const store = createStore({ api: window.FurFarApi, isSignedIn });

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
  })[character]);

  const loginUrl = (returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`) => {
    const base = document.body.dataset.loginUrl || '/customer_authentication/login';
    try {
      const url = new URL(base, window.location.origin);
      url.searchParams.set('return_to', returnTo);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      const separator = base.includes('?') ? '&' : '?';
      return `${base}${separator}return_to=${encodeURIComponent(returnTo)}`;
    }
  };

  const productFromDataset = (element) => ({
    shopify_product_id: String(element.dataset.productId || ''),
    shopify_variant_id: element.dataset.productVariantId || null,
    product_handle: element.dataset.productHandle || '',
    product_title: element.dataset.productTitle || '',
    product_url: element.dataset.productUrl || '',
    image_url: element.dataset.productImage || '',
    display_price: element.dataset.productPrice || '',
  });

  const current = () => store.snapshot();

  const renderPetSelect = (root) => {
    const { pets } = current();
    const wrap = root.querySelector('[data-wishlist-pet-wrap]');
    const select = root.querySelector('[data-wishlist-pet]');
    if (!wrap || !select) return;
    const previous = select.value;
    if (root.dataset.wishlistCompact === 'true' || pets.length < 2) {
      wrap.hidden = true;
      select.innerHTML = '';
      return;
    }
    wrap.hidden = false;
    const selected = store.defaultListId(previous);
    select.innerHTML = pets.map((pet) => (
      `<option value="${escapeHtml(pet.id)}"${String(pet.id) === selected ? ' selected' : ''}>${escapeHtml(pet.pet_name || 'Pet')}'s wishlist</option>`
    )).join('');
  };

  const syncButton = (root) => {
    const { pets } = current();
    const select = root.querySelector('[data-wishlist-pet]');
    const button = root.querySelector('[data-wishlist-add]');
    if (!button) return;
    const listId = pets.length ? store.defaultListId(select && select.value) : 'personal';
    if (select && pets.length > 1) select.value = listId;
    let product;
    try {
      product = store.normalizeProduct(productFromDataset(root));
    } catch (error) {
      button.disabled = true;
      return;
    }
    const saved = store.itemsFor(listId).some((item) => (
      String(item.shopify_product_id) === product.shopify_product_id
      && String(item.shopify_variant_id || '') === String(product.shopify_variant_id || '')
    ));
    const pet = pets.find((candidate) => String(candidate.id) === listId);
    button.textContent = root.dataset.wishlistCompact === 'true'
      ? (saved ? 'Saved' : 'Wishlist')
      : pets.length
        ? `${saved ? 'Saved to' : 'Add to'} ${(pet && pet.pet_name) || 'pet'}'s wishlist`
        : (saved ? 'Saved to wishlist' : 'Add to wishlist');
    button.setAttribute('aria-pressed', String(saved));
    button.classList.toggle('is-saved', saved);
  };

  const bindControl = (root) => {
    if (!root || root.dataset.wishlistBound === 'true') return;
    root.dataset.wishlistBound = 'true';
    const select = root.querySelector('[data-wishlist-pet]');
    const button = root.querySelector('[data-wishlist-add]');
    const status = root.querySelector('[data-wishlist-status]');
    if (select) select.addEventListener('change', () => syncButton(root));
    if (button) button.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isSignedIn) {
        window.location.href = loginUrl();
        return;
      }
      const listId = current().pets.length
        ? store.defaultListId(select && select.value)
        : 'personal';
      try {
        const pending = store.toggle(listId, productFromDataset(root));
        syncButton(root);
        if (status) status.textContent = 'Saving…';
        const result = await pending;
        refreshAll();
        if (status) status.textContent = result.added ? 'Saved' : 'Removed';
        document.dispatchEvent(new CustomEvent('ff:wishlist-changed', { detail: result }));
      } catch (error) {
        refreshAll();
        if (status) status.textContent = 'Could not update the wishlist. Your saved list was restored.';
      }
    });
  };

  const renderItems = (items, listId) => {
    if (!items.length) return '<p class="ff-hub__recommend-note">No products saved yet.</p>';
    return `<div class="ff-wish__grid">${items.map((item) => `
      <article class="ff-wish__item" data-wish-item data-list-id="${escapeHtml(listId)}" data-product-id="${escapeHtml(item.shopify_product_id)}">
        <a href="${escapeHtml(item.product_url || '#')}">
          ${item.image_url
            ? `<img class="js-white-bg-check" crossorigin="anonymous" src="${escapeHtml(item.image_url)}" alt="${escapeHtml(item.product_title)}" width="120" height="120" loading="lazy">`
            : `<span class="ff-hub__pet-fallback" aria-hidden="true">${escapeHtml((item.product_title || 'P').slice(0, 1))}</span>`}
        </a>
        <div><h3><a href="${escapeHtml(item.product_url || '#')}">${escapeHtml(item.product_title || 'Product')}</a></h3><p>${escapeHtml(item.display_price || '')}</p></div>
        <button type="button" class="button button--outline" data-wish-remove>Remove</button>
      </article>`).join('')}</div>`;
  };

  const renderAccountWishlist = () => {
    const root = document.querySelector('[data-account-wishlist]');
    if (!root) return;
    const wishlistUrl = root.dataset.wishlistUrl || '/pages/myaccount#wishlist';
    const passportUrl = root.dataset.passportUrl || '/pages/pet-passport';
    if (!isSignedIn) {
      root.innerHTML = `<div class="ff-hub__empty"><p>Sign in to save wishlists for you and your pets.</p><a class="button" href="${escapeHtml(loginUrl(wishlistUrl))}">Sign in / create account</a></div>`;
      return;
    }
    const { pets, status } = current();
    if (status === 'idle' || status === 'loading') {
      root.innerHTML = '<p class="ff-hub__recommend-note" role="status">Loading your wishlists…</p>';
      return;
    }
    const summary = store.counts();
    const sections = [];
    if (!pets.length) {
      sections.push(`<section class="ff-wish__section"><div class="ff-hub__panel-head"><h2>My wishlist</h2><p class="ff-hub__panel-note">${summary.personal} saved · <a href="${escapeHtml(passportUrl)}">Create a Pet Passport</a> for personalised lists</p></div>${renderItems(store.itemsFor('personal'), 'personal')}</section>`);
    } else {
      pets.forEach((pet) => {
        const items = store.itemsFor(String(pet.id));
        sections.push(`<section class="ff-wish__section"><div class="ff-hub__panel-head"><h2>${escapeHtml(pet.pet_name || 'Pet')}'s wishlist</h2><p class="ff-hub__panel-note">${items.length} saved</p></div>${renderItems(items, pet.id)}</section>`);
      });
      if (summary.personal) sections.push(`<section class="ff-wish__section"><div class="ff-hub__panel-head"><h2>General wishlist</h2><p class="ff-hub__panel-note">${summary.personal} saved</p></div>${renderItems(store.itemsFor('personal'), 'personal')}</section>`);
    }
    root.innerHTML = `<div class="ff-hub__stats" style="margin-bottom:22px"><span class="ff-hub__stat">${summary.total} saved</span><span class="ff-hub__stat">${pets.length} pet list${pets.length === 1 ? '' : 's'}</span></div>${sections.join('')}`;
    root.querySelectorAll('[data-wish-remove]').forEach((button) => {
      button.addEventListener('click', async () => {
        const card = button.closest('[data-wish-item]');
        if (!card) return;
        const item = store.itemsFor(card.dataset.listId).find((candidate) => String(candidate.shopify_product_id) === card.dataset.productId);
        if (!item) return;
        button.disabled = true;
        try {
          await store.toggle(card.dataset.listId, item);
          refreshAll();
          document.dispatchEvent(new CustomEvent('ff:wishlist-changed'));
        } catch (error) {
          refreshAll();
        }
      });
    });
  };

  const renderDashboardPreview = () => {
    const root = document.querySelector('[data-account-wishlist-preview]');
    if (!root) return;
    const wishlistUrl = root.dataset.wishlistUrl || '/pages/myaccount#wishlist';
    if (!isSignedIn) {
      root.innerHTML = `<p class="ff-hub__recommend-note">Sign in to save favourites for you and your pets.</p><a href="${escapeHtml(loginUrl(wishlistUrl))}">Sign in →</a>`;
      return;
    }
    const summary = store.counts();
    root.innerHTML = summary.total
      ? `<div class="ff-hub__stats"><span class="ff-hub__stat">${summary.total} saved</span>${summary.pets.filter((row) => row.count).map((row) => `<span class="ff-hub__stat">${escapeHtml(row.name)} · ${row.count}</span>`).join('')}</div><p class="ff-hub__panel-actions"><a class="button" href="${escapeHtml(wishlistUrl)}">View wishlists →</a></p>`
      : `<p class="ff-hub__recommend-note">Save products from any product page to see them here.</p><a class="button button--outline" href="${escapeHtml(wishlistUrl)}">Open wishlist</a>`;
  };

  const refreshAll = () => {
    document.querySelectorAll('[data-wishlist-control]').forEach((root) => {
      bindControl(root);
      renderPetSelect(root);
      syncButton(root);
    });
    renderAccountWishlist();
    renderDashboardPreview();
  };

  window.FurFarWishlist = {
    WishlistStateError,
    createStore,
    counts: () => store.counts(),
    loadPets: () => current().pets,
    loadStore: () => current(),
    refresh: refreshAll,
    setState: (data) => store.setState(data),
    toggleItem: (listId, product) => store.toggle(listId, product),
  };

  document.addEventListener('ff:storefront-state', (event) => {
    try {
      store.setState(event.detail);
      refreshAll();
    } catch (error) {
      // Ignore malformed state; the signed client will surface the request error.
    }
  });
  document.addEventListener('DOMContentLoaded', async () => {
    refreshAll();
    if (!isSignedIn || !window.FurFarApi) return;
    try {
      const result = await window.FurFarApi.state();
      store.setState(result.data);
    } catch (error) {
      // The client handles sign-in redirects and exposes only safe errors.
    }
    refreshAll();
  });
})();
